/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import PropTypes from 'prop-types';

export const ACCESS_CONTROLS = {
  MONITOR_CONNECT_VIEW: 'MONITOR_CONNECT_VIEW',
  MONITOR_DASHBOARD_MANAGE: 'MONITOR_DASHBOARD_MANAGE',
  MONITOR_DASHBOARD_PIN: 'MONITOR_DASHBOARD_PIN',
  MONITOR_DASHBOARD_VIEW: 'MONITOR_DASHBOARD_VIEW',
  MONITOR_ENTITIES_MANAGE: 'MONITOR_ENTITIES_MANAGE',
  MONITOR_ENTITIES_VIEW: 'MONITOR_ENTITIES_VIEW',
  MONITOR_ALERTS_MANAGE: 'MONITOR_ALERTS_MANAGE',
  MONITOR_ALERTS_VIEW: 'MONITOR_ALERTS_VIEW',
  MONITOR_DATA_MANAGE: 'MONITOR_DATA_MANAGE',
  MONITOR_DATA_VIEW: 'MONITOR_DATA_VIEW',
  MONITOR_HOME_VIEW: 'MONITOR_HOME_VIEW',
  MONITOR_SERVICES_MANAGE: 'MONITOR_SERVICES_MANAGE',
  MONITOR_SERVICES_VIEW: 'MONITOR_SERVICES_VIEW',
  MONITOR_USERS_MANAGE: 'MONITOR_USERS_MANAGE',
  MONITOR_USERS_VIEW: 'MONITOR_USERS_VIEW',
  DEVICE_DEVICES_CREATE: 'DEVICE_DEVICES_CREATE',
  DEVICE_DEVICES_DELETE: 'DEVICE_DEVICES_DELETE',
  DEVICE_DEVICES_UPDATE: 'DEVICE_DEVICES_UPDATE',
  DEVICE_DEVICES_VIEW: 'DEVICE_DEVICES_VIEW',
  DEVICE_TYPES_MANAGE: 'DEVICE_TYPES_MANAGE',
  DEVICE_TYPES_VIEW: 'DEVICE_TYPES_VIEW',
  IM_LOGICAL_INTF_MANAGE: 'IM_LOGICAL_INTF_MANAGE',
  IM_LOGICAL_INTF_VIEW: 'IM_LOGICAL_INTF_VIEW',
};

export const ErrorPropTypes = PropTypes.oneOfType([
  PropTypes.shape({
    i18nMessage: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    values: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    message: PropTypes.string,
  }),
  PropTypes.string,
]);

export const PlatformConnectionPropTypes = PropTypes.shape({
  org: PropTypes.string,
  dashboardUrl: PropTypes.string,
  deviceTypes: PropTypes.number,
  devices: PropTypes.number,
  dataItems: PropTypes.number,
});

export const WorkspacePropTypes = PropTypes.shape({
  /** set of permissions for the current workspace */
  role: PropTypes.string,
  url: PropTypes.string,
  applications: PropTypes.objectOf(
    PropTypes.shape({ url: PropTypes.string, role: PropTypes.string })
  ),
});

export const RoutePropTypes = PropTypes.shape({
  /** URL of the profile page */
  profile: PropTypes.string,
  /** URL of the navigator page */
  navigator: PropTypes.string,
  /** URL of the admin page */
  admin: PropTypes.string,
  /** URL keyed by the application id */
  applications: PropTypes.objectOf(PropTypes.string),
});

export const UserDataPropTypes = PropTypes.shape({
  /** unique user id */
  id: PropTypes.string,
  /** details of which services exist in the tenant */
  serviceConfig: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  salesURL: PropTypes.string,
  /** Display name to show in the Home page */
  user: PropTypes.shape({ displayName: PropTypes.string }),
  isICP: PropTypes.bool,
  docURL: PropTypes.string,
  /** MAS user routes */
  routes: RoutePropTypes,
  /** MAS workspaces that are valid */
  workspaces: PropTypes.objectOf(WorkspacePropTypes),
  /** array of access controls for the user that match the ACCESS_CONTROLS constant */
  accessControls: PropTypes.arrayOf(PropTypes.string),
});

export const ApplicationErrorsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    timestamp: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
  })
);
/**
 * Used for the tactical user resource access control usecase
 *
 */
export const UserResourcesPropTypes = PropTypes.shape({
  ORGANIZATION: PropTypes.arrayOf(PropTypes.string),
  SITE: PropTypes.arrayOf(PropTypes.string),
  SYSTEM: PropTypes.arrayOf(PropTypes.string),
  LOCATION_TYPE: PropTypes.arrayOf(PropTypes.string),
  LOCATION: PropTypes.arrayOf(PropTypes.string),
  ASSET_TYPE: PropTypes.arrayOf(PropTypes.string),
  ASSET: PropTypes.arrayOf(PropTypes.string),
  DEVICE_TYPE: PropTypes.arrayOf(PropTypes.string),
  DEVICE: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  CATALOG_FUNCTION: PropTypes.arrayOf(PropTypes.number),
  SERVICE: PropTypes.arrayOf(PropTypes.oneOf(['MAXIMO_WORK_ORDER', 'FACTORY_TALK'])),
});
